import { useState, useRef, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { Send, Mail, Phone, MapPin, CheckCircle, AlertCircle } from 'lucide-react';
import { pushPublicMessage, readData } from '@/firebase/database';

interface ContactData {
  heading: string;
  subheading: string;
  description: string;
  talkTitle: string;
  talkDescription: string;
  email: string;
  phone: string;
  location: string;
  availabilityTitle: string;
  availabilitySubtext: string;
}

const defaultContact: ContactData = {
  heading: 'Contact',
  subheading: 'Get in touch',
  description: 'Have a project in mind? Let\'s build something amazing together.',
  talkTitle: 'Let\'s talk',
  talkDescription: 'I\'m always open to discussing new projects, creative ideas, or opportunities.',
  email: 'mustakim@example.com',
  phone: '+880 1234-567890',
  location: 'Dhaka, Bangladesh',
  availabilityTitle: 'Available for freelance',
  availabilitySubtext: 'Currently accepting new projects',
};

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [contact, setContact] = useState<ContactData>(defaultContact);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });

  useEffect(() => {
    readData('contact').then((data) => {
      if (data) setContact({ ...defaultContact, ...data });
    });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('sending');
    try {
      await pushPublicMessage({ ...form, timestamp: new Date().toISOString(), read: false });
      setStatus('success');
      setForm({ name: '', email: '', subject: '', message: '' });
      setTimeout(() => setStatus('idle'), 4000);
    } catch (err) {
      console.error('Message send error:', err);
      setStatus('error');
      setTimeout(() => setStatus('idle'), 4000);
    }
  };

  const inputClass = 'w-full bg-muted border border-border rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 text-foreground placeholder:text-muted-foreground focus:border-primary/50 focus:ring-1 focus:ring-primary/20 focus:outline-none transition-all duration-300 font-mono text-xs sm:text-sm';

  return (
    <section id="contact" className="py-16 sm:py-24 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6" ref={ref}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }} className="text-center mb-10 sm:mb-16">
          <p className="section-tag text-xs">{contact.subheading}</p>
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold">
            {contact.heading.split(' ')[0]} <span className="gradient-text">{contact.heading.split(' ').slice(1).join(' ') || 'Me'}</span>
          </h2>
          <div className="w-20 h-0.5 mx-auto mt-4 bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
          <p className="text-muted-foreground mt-4 max-w-lg mx-auto text-xs sm:text-sm">{contact.description}</p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, x: -30 }} animate={inView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.6, delay: 0.2 }} className="space-y-6 sm:space-y-8">
            <div>
              <h3 className="font-display text-xl sm:text-2xl font-bold mb-2">
                {contact.talkTitle.split(' ')[0]} <span className="gradient-text">{contact.talkTitle.split(' ').slice(1).join(' ')}</span>
              </h3>
              <p className="text-muted-foreground text-xs sm:text-sm leading-relaxed">{contact.talkDescription}</p>
            </div>

            {[
              { icon: Mail, label: 'Email', value: contact.email, href: `mailto:${contact.email}` },
              { icon: Phone, label: 'Phone', value: contact.phone, href: `tel:${contact.phone}` },
              { icon: MapPin, label: 'Location', value: contact.location, href: '#' },
            ].map(({ icon: Icon, label, value, href }) => (
              <a key={label} href={href} className="flex items-center gap-3 sm:gap-4 group">
                <div className="p-2.5 sm:p-3 rounded-xl glass neon-border group-hover:border-primary/30 transition-all duration-300">
                  <Icon size={18} className="text-primary" />
                </div>
                <div>
                  <p className="text-[10px] sm:text-xs font-mono text-muted-foreground">{label}</p>
                  <p className="text-sm font-medium group-hover:text-primary transition-colors duration-300">{value}</p>
                </div>
              </a>
            ))}

            <div className="glass neon-border rounded-xl p-3 sm:p-4 flex items-center gap-3">
              <div className="w-3 h-3 rounded-full bg-primary animate-pulse" />
              <div>
                <p className="font-mono text-xs sm:text-sm font-bold text-primary">{contact.availabilityTitle}</p>
                <p className="text-[10px] sm:text-xs text-muted-foreground">{contact.availabilitySubtext}</p>
              </div>
            </div>
          </motion.div>

          <motion.form initial={{ opacity: 0, x: 30 }} animate={inView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.6, delay: 0.3 }}
            onSubmit={handleSubmit} className="glass neon-border rounded-2xl p-5 sm:p-8 space-y-3 sm:space-y-4">
            <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label className="block text-[10px] sm:text-xs font-mono text-muted-foreground mb-1.5 sm:mb-2">Name</label>
                <input type="text" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="John Doe" className={inputClass} />
              </div>
              <div>
                <label className="block text-[10px] sm:text-xs font-mono text-muted-foreground mb-1.5 sm:mb-2">Email</label>
                <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="john@example.com" className={inputClass} />
              </div>
            </div>
            <div>
              <label className="block text-[10px] sm:text-xs font-mono text-muted-foreground mb-1.5 sm:mb-2">Subject</label>
              <input type="text" required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Project inquiry" className={inputClass} />
            </div>
            <div>
              <label className="block text-[10px] sm:text-xs font-mono text-muted-foreground mb-1.5 sm:mb-2">Message</label>
              <textarea required rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Tell me about your project..." className={`${inputClass} resize-none`} />
            </div>
            <button type="submit" disabled={status === 'sending'} className="btn-neon-solid w-full flex items-center justify-center gap-2 py-2.5 sm:py-3 text-xs sm:text-sm">
              {status === 'sending' ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                  Sending...
                </span>
              ) : (
                <span className="flex items-center gap-2"><Send size={14} /> Send Message</span>
              )}
            </button>
            {status === 'success' && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-2 text-primary text-xs sm:text-sm font-mono">
                <CheckCircle size={16} /> Message sent successfully!
              </motion.div>
            )}
            {status === 'error' && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-2 text-destructive text-xs sm:text-sm font-mono">
                <AlertCircle size={16} /> Something went wrong. Try again.
              </motion.div>
            )}
          </motion.form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
